import { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Trash2, Upload, Plus } from 'lucide-react';
import { MediaPreview } from './MediaPreview';
import { MAX_MEDIA } from '../lib/posts';

interface Props {
  urls: string[];
  onChange: (urls: string[]) => void;
  upload: (file: File) => Promise<string>;
  onError: (message: string) => void;
}

/** Files of an item, in order: one image or video, or a carousel of up to 20. */
export function MediaListEditor({ urls, onChange, upload, onError }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(0);
  const room = MAX_MEDIA - urls.length;

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const accepted = files.slice(0, room);
    if (files.length > room) onError(`Máximo de ${MAX_MEDIA} ficheiros: ${files.length - room} não foram adicionados`);

    setUploading(accepted.length);
    const added: string[] = [];
    try {
      // In order, so the carousel keeps the order they were picked in
      for (const file of accepted) {
        added.push(await upload(file));
        setUploading(n => n - 1);
      }
    } catch {
      onError('Erro ao carregar um dos ficheiros');
    } finally {
      if (added.length) onChange([...urls, ...added]);
      setUploading(0);
    }
  }

  function move(from: number, to: number) {
    if (to < 0 || to >= urls.length) return;
    const next = [...urls];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  }

  return (
    <div>
      {urls.length === 0 ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading > 0}
          className="w-full h-32 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-[var(--ch-ink)] hover:text-[var(--ch-ink)] transition-colors"
        >
          <Upload className="w-5 h-5" />
          <span className="text-xs font-medium">
            {uploading > 0 ? `A enviar ${uploading}...` : 'Carregar ficheiros'}
          </span>
          <span className="text-[10px]">Imagem, vídeo ou vários para um carrossel (até {MAX_MEDIA})</span>
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {urls.map((url, i) => (
            <div key={`${url}-${i}`} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
              <MediaPreview url={url} className="w-full h-full object-cover" />
              <span className="absolute top-1 left-1 min-w-[18px] h-[18px] px-1 rounded-full bg-black/60 text-white text-[10px] font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-gradient-to-t from-black/60 to-transparent">
                <div className="flex gap-1">
                  <button
                    type="button"
                    aria-label="Mover para trás"
                    onClick={() => move(i, i - 1)}
                    disabled={i === 0}
                    className="p-1 rounded bg-white/90 text-gray-700 disabled:opacity-30"
                  >
                    <ArrowLeft className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    aria-label="Mover para a frente"
                    onClick={() => move(i, i + 1)}
                    disabled={i === urls.length - 1}
                    className="p-1 rounded bg-white/90 text-gray-700 disabled:opacity-30"
                  >
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
                <button
                  type="button"
                  aria-label="Remover"
                  onClick={() => onChange(urls.filter((_, j) => j !== i))}
                  className="p-1 rounded bg-red-500 text-white"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
          {room > 0 && (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading > 0}
              className="aspect-square rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-[var(--ch-ink)] hover:text-[var(--ch-ink)] transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="text-[10px] font-medium">{uploading > 0 ? `A enviar ${uploading}...` : 'Adicionar'}</span>
            </button>
          )}
        </div>
      )}
      {urls.length > 0 && (
        <p className="text-[10px] text-gray-400 mt-1.5">
          {urls.length}/{MAX_MEDIA} {urls.length === 1 ? 'ficheiro' : 'ficheiros'}
          {urls.length > 1 && ' · a ordem é a do carrossel'}
        </p>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={handleFiles}
      />
    </div>
  );
}
